import { clear, createStore, del, get, set, UseStore } from 'idb-keyval';
import { sessionStorage } from './storage';

export const DEFAULT_CACHE_REQUEST_TIMEOUT_MS = 60 * 1000; // 60 seconds

export interface ICacheOptions {
  timeout: number;
}

export const defaultCacheOptions: ICacheOptions = {
  timeout: DEFAULT_CACHE_REQUEST_TIMEOUT_MS,
};

export function detectIdb() {
  return new Promise<void>((yes, not) => {
    if (!window.indexedDB) {
      not(new Error('indexedDB not supported'));
      return;
    }
    const db = window.indexedDB.open('test');
    if (!db) {
      not(new Error('indexedDB not supported'));
      return;
    }
    db.onerror = function () {
      not(new Error('indexedDB not supported'));
    };
    db.onsuccess = function () {
      db.result.close();
      window.indexedDB.deleteDatabase(db.result.name);
      yes();
    };
  });
}

function timeoutPromise<T = any>(promise: Promise<T>, timeout: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const handle = setTimeout(() => {
      reject(new Error('Execution timed out'));
    }, timeout);
    promise
      .then((value) => {
        clearTimeout(handle);
        resolve(value);
      })
      .catch((reason) => {
        clearTimeout(handle);
        reject(reason as Error);
      });
  });
}

export function createCacheService(options: ICacheOptions = defaultCacheOptions) {
  const { timeout } = options;
  let isReady = false;
  let isSupported = false;
  let store: UseStore;
  async function init() {
    if (isReady) {
      return;
    }
    try {
      await detectIdb();
      store = createStore('neuronation', 'cache-store');
      isSupported = true;
    } catch (error) {
      console.log('Cache initialization failed.', (error as Error).message);
    }
    isReady = true;
  }

  function save<T = any>(key: string, value: T) {
    if (isSupported) {
      return timeoutPromise(set(key, value, store), timeout);
    }
    return Promise.resolve(sessionStorage.save(key, value));
  }

  function load<T = any>(key: string) {
    if (isSupported) {
      return timeoutPromise(get<T>(key, store), timeout);
    }
    const value = sessionStorage.load<T>(key);
    return Promise.resolve(value === null ? undefined : value);
  }

  function deleteRecord(key: string) {
    if (isSupported) {
      return timeoutPromise(del(key, store), timeout);
    }
    return Promise.resolve(sessionStorage.remove(key));
  }

  function clearStore() {
    if (isSupported) {
      return timeoutPromise(clear(store), timeout);
    }
    return Promise.resolve(sessionStorage.clear());
  }

  return {
    init,
    save,
    load,
    delete: deleteRecord,
    clear: clearStore,
  };
}

const CacheService = createCacheService();
export type CacheStorage = typeof CacheService;

export default CacheService;
